import { useState } from 'react'
import { CheckCircle2, Clock, ExternalLink, Loader2, ShieldCheck, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { Button } from '@/components/ui/button'
import { useProfile } from '@/api/profile'
import { useCompanyPhoneNumbers } from '@/api/company'
import { useTrustCenter, type EtatEnregistrement } from '@/api/trustCenter'
import { TollfreeSmsDialog } from '@/components/manager/numbers/TollfreeSms'
import { useTollfreeSms } from '@/api/phoneNumbers'
import { UsRegistrationDialog } from './UsRegistrationDialog'
import { UsRegistrationDetails } from './UsRegistrationDetails'
import { cn } from '@/lib/utils'

/**
 * Centre de confiance : « Vos enregistrements ». Trois blocs, comme chez les
 * concurrents : textos vers les États-Unis (marque, campagne, STIR/SHAKEN),
 * numéros sans frais, étiquette « Arnaque probable ». On parle des opérateurs.
 */
export function TrustCenterView() {
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const { data: profile } = useProfile()
  const companyId = profile?.company
  const { data: etat, isLoading } = useTrustCenter(companyId)
  const { data: numerosData } = useCompanyPhoneNumbers(companyId)
  const numeros = numerosData?.phone_numbers ?? []
  const [usOuvert, setUsOuvert] = useState(false)
  const [detailsOuverts, setDetailsOuverts] = useState(false)
  const [sansFraisId, setSansFraisId] = useState<number | null>(null)

  const dateLocale = locale === 'fr' ? 'fr-CA' : 'en-CA'
  const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(dateLocale, { day: 'numeric', month: 'short', year: 'numeric' }) : '')
  const argent = (cents: number) => new Intl.NumberFormat(dateLocale, { style: 'currency', currency: 'CAD' }).format(cents / 100)

  if (isLoading || !etat) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    )
  }

  const us = etat.us
  const peutDeposer = us.status === 'none' || us.status === 'rejected'
  const sansFraisChoisi = numeros.find((n) => n.id === sansFraisId) ?? null

  return (
    <div className="h-full flex flex-col">
    <div className="flex-1 min-h-0 overflow-y-auto">
    <div className="px-6 lg:px-8 py-6 max-w-4xl space-y-10">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-blue-600" />
          {t('manager.trust.title')}
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-2xl">{t('manager.trust.subtitle')}</p>
      </header>

      {/* ─── 1. Textos vers les États-Unis ─────────────────────────── */}
      <section className="space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('manager.trust.us.title')}</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-2xl">{t('manager.trust.us.subtitle')}</p>
          </div>
          {peutDeposer && (
            <Button type="button" onClick={() => setUsOuvert(true)} className="bg-blue-600 hover:bg-blue-700 text-white" disabled={etat.local_numbers.length === 0}>
              {us.status === 'rejected' ? t('manager.trust.us.resubmit') : t('manager.trust.us.register')}
            </Button>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800">
          <Ligne label={t('manager.trust.us.brand')} help={t('manager.trust.us.brandHelp')} etat={us.brand} />
          <Ligne label={t('manager.trust.us.campaign')} help={t('manager.trust.us.campaignHelp')} etat={us.campaign} />
          <Ligne label="STIR/SHAKEN" help={t('manager.trust.us.shakenHelp')} etat={us.shaken} />
        </div>

        {us.status === 'rejected' && us.reason && (
          <div className="rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-4 py-3 text-sm text-red-800 dark:text-red-300">
            {us.reason}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-slate-500 dark:text-slate-400">
          <span>
            {us.status === 'none'
              ? t('manager.trust.us.fees', { setup: argent(us.setup_fee_cents), monthly: argent(us.monthly_fee_cents || 300) })
              : us.submitted_at
                ? t('manager.trust.us.submittedOn', { date: formatDate(us.submitted_at) })
                : ''}
          </span>
          {us.details && (
            <button type="button" onClick={() => setDetailsOuverts(true)} className="text-blue-600 hover:text-blue-700 font-medium">
              {t('manager.trust.details')}
            </button>
          )}
        </div>
        {etat.local_numbers.length === 0 && us.status === 'none' && (
          <p className="text-xs text-slate-400">{t('manager.trust.us.noLocal')}</p>
        )}
      </section>

      {/* ─── 2. Numéros sans frais ────────────────────────────────── */}
      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('manager.trust.tollfree.title')}</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 max-w-2xl">{t('manager.trust.tollfree.subtitle')}</p>
        </div>
        {etat.tollfree_numbers.length === 0 ? (
          <p className="text-sm text-slate-400 rounded-xl border border-dashed border-slate-200 dark:border-slate-800 px-4 py-6 text-center">{t('manager.trust.tollfree.none')}</p>
        ) : (
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800">
            {etat.tollfree_numbers.map((n) => {
              const s = n.status === 'unknown' ? 'none' : n.status
              return (
                <div key={n.id} className="px-4 py-3 flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-sm text-slate-900 dark:text-white">{n.label || t('manager.trust.tollfree.number')}</p>
                    <p className="text-xs text-slate-500">{formatNumero(n.phone_number)}{n.checked_at && s === 'pending' ? ` · ${t('manager.trust.tollfree.submitted')}` : ''}</p>
                    {s === 'rejected' && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">{n.reason || t('manager.trust.tollfree.rejectedHint')}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <Pastille etat={s} />
                    {(s === 'none' || s === 'rejected') && (
                      <Button type="button" size="sm" variant={s === 'rejected' ? 'default' : 'outline'} onClick={() => setSansFraisId(n.id)}>
                        {s === 'rejected' ? t('manager.trust.us.resubmit') : t('manager.numbers.tollfreeSms.activate')}
                      </Button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
        <p className="text-xs text-slate-400">{t('manager.trust.tollfree.free')}</p>
      </section>

      {/* ─── 3. Arnaque probable ──────────────────────────────────── */}
      <section className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-5 py-4 flex flex-wrap items-center justify-between gap-4">
        <div className="max-w-xl">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('manager.trust.scam.title')}</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">{t('manager.trust.scam.text')}</p>
        </div>
        <a href="https://www.freecallerregistry.com/fcr/" target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-slate-300 dark:border-slate-700 text-sm font-medium text-slate-900 dark:text-white hover:bg-slate-50 dark:hover:bg-slate-800">
          {t('manager.trust.scam.cta')} <ExternalLink className="h-4 w-4" />
        </a>
      </section>

      <UsRegistrationDialog open={usOuvert} onClose={() => setUsOuvert(false)} companyId={companyId} etat={etat} />
      <UsRegistrationDetails open={detailsOuverts} onClose={() => setDetailsOuverts(false)} details={us.details} />
      {sansFraisChoisi && (
        <SansFraisDialogue numero={sansFraisChoisi} companyId={companyId} onClose={() => setSansFraisId(null)} />
      )}
    </div>
    </div>
    </div>
  )
}

/** Charge le préremplissage du sans-frais choisi puis ouvre la même fenêtre que dans la fiche du numéro. */
function SansFraisDialogue({ numero, companyId, onClose }: { numero: { id: number }; companyId: number | undefined; onClose: () => void }) {
  const { data: numerosData } = useCompanyPhoneNumbers(companyId)
  const n = numerosData?.phone_numbers.find((x) => x.id === numero.id)
  const etat = useTollfreeSms(companyId, numero.id, true)
  if (!n) return null
  return <TollfreeSmsDialog open onClose={onClose} number={n} companyId={companyId} prefill={etat.data?.prefill} />
}

function Ligne({ label, help, etat }: { label: string; help: string; etat: EtatEnregistrement }) {
  return (
    <div className="px-4 py-3 flex items-center justify-between gap-4">
      <div className="min-w-0">
        <p className="font-medium text-sm text-slate-900 dark:text-white">{label}</p>
        <p className="text-xs text-slate-500 dark:text-slate-400">{help}</p>
      </div>
      <Pastille etat={etat} />
    </div>
  )
}

export function Pastille({ etat }: { etat: EtatEnregistrement }) {
  const { t } = useTranslation()
  const Icone = etat === 'approved' ? CheckCircle2 : etat === 'rejected' ? XCircle : Clock
  return (
    <span className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium shrink-0',
      etat === 'approved' && 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400',
      etat === 'pending' && 'bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300',
      etat === 'rejected' && 'bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-300',
      etat === 'none' && 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400')}>
      <Icone className="h-3.5 w-3.5" />
      {t(`manager.trust.status.${etat}`)}
    </span>
  )
}

function formatNumero(n: string) {
  const d = n.replace(/\D/g, '')
  return d.length === 11 && d[0] === '1' ? `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}` : n
}

